import type { LucideIcon } from 'lucide-react'
import { Ellipsis, Home, Megaphone, Newspaper, PieChart, Search, TrendingUp, Wallet, Settings } from 'lucide-react'

import { APP_ROUTES } from '@/common/constants'
import { NAVIGATION_COPY } from '@/locales/en'
import type { MobileBottomNavItem } from './MobileBottomNav'
import { useFeatureFlags } from '../featureFlags/featureFlags'

type Audience = 'all' | 'standard' | 'adviser'
type FeatureFlagKey = keyof ReturnType<typeof useFeatureFlags>

type AppMobileNavItem = {
    key: string
    label: string
    description: string
    to: string
    icon: LucideIcon
    audience: Audience
    isActive: (pathname: string) => boolean
    featureFlag?: FeatureFlagKey
}

const APP_MOBILE_NAV_ITEMS: AppMobileNavItem[] = [
    {
        key: 'dashboard',
        label: NAVIGATION_COPY.mobileBottomNav.dashboard,
        description: '',
        to: APP_ROUTES.DASHBOARD,
        icon: Home,
        audience: 'all',
        isActive: (pathname: string) => pathname === APP_ROUTES.DASHBOARD,
    },
    {
        key: 'campaigns',
        label: NAVIGATION_COPY.mobileBottomNav.campaigns,
        description: '',
        to: APP_ROUTES.CAMPAIGNS,
        icon: Megaphone,
        audience: 'all',
        isActive: (pathname: string) => pathname.startsWith(APP_ROUTES.CAMPAIGNS),
    },
    {
        key: 'portfolio',
        label: NAVIGATION_COPY.mobileBottomNav.portfolio,
        description: '',
        to: APP_ROUTES.PORTFOLIO,
        icon: PieChart,
        audience: 'all',
        isActive: (pathname: string) => pathname.startsWith(APP_ROUTES.PORTFOLIO),
    },
    {
        key: 'find-stocks',
        label: NAVIGATION_COPY.findStocks,
        description: NAVIGATION_COPY.mobileMoreNav.findStocksDescription,
        to: APP_ROUTES.FIND_STOCKS,
        icon: Search,
        audience: 'standard',
        isActive: (pathname: string) => pathname.startsWith(APP_ROUTES.FIND_STOCKS),
    },
    {
        key: 'wallet',
        label: NAVIGATION_COPY.mobileBottomNav.wallet,
        description: NAVIGATION_COPY.mobileMoreNav.walletDescription,
        to: APP_ROUTES.WALLET,
        icon: Wallet,
        audience: 'all',
        isActive: (pathname: string) => pathname.startsWith(APP_ROUTES.WALLET),
    },
    {
        key: 'invest',
        label: NAVIGATION_COPY.mobileBottomNav.invest,
        description: NAVIGATION_COPY.mobileMoreNav.investDescription,
        to: APP_ROUTES.INVEST,
        icon: TrendingUp,
        audience: 'standard',
        isActive: (pathname: string) => pathname.startsWith(APP_ROUTES.INVEST),
    },
    {
        key: 'blogs',
        label: NAVIGATION_COPY.blogs,
        description: NAVIGATION_COPY.mobileMoreNav.blogsDescription,
        to: APP_ROUTES.BLOGS,
        icon: Newspaper,
        audience: 'standard',
        isActive: (pathname: string) => pathname.startsWith(APP_ROUTES.BLOGS),
    },
    {
        key: 'account',
        label: NAVIGATION_COPY.account.pageTitle,
        description: NAVIGATION_COPY.account.moreDescription,
        to: APP_ROUTES.ACCOUNT,
        icon: Settings,
        audience: 'all',
        isActive: (pathname: string) => pathname.startsWith(APP_ROUTES.ACCOUNT),
    },
]

type MobileRole = 'adviser' | 'standard'

const BOTTOM_NAV_KEYS: Record<MobileRole, string[]> = {
    adviser: ['dashboard', 'campaigns', 'portfolio', 'wallet'],
    standard: ['dashboard', 'campaigns', 'portfolio', 'find-stocks'],
}

const EXCLUDED_MORE_KEYS = new Set(['profile', 'rewards', 'support'])

const isVisibleForRole = (item: AppMobileNavItem, role: MobileRole) => {
    if (item.audience === 'all') return true
    return item.audience === role
}

const getVisibleItems = (role: MobileRole) => APP_MOBILE_NAV_ITEMS.filter((item) => isVisibleForRole(item, role))

const getFeatureFlaggedVisibleItems = (featureFlag: string | undefined, featureFlags: Record<string, boolean>) => {
    if (!featureFlag) return true
    return featureFlags[featureFlag] === true
}

export const getMobileMoreNavItems = (isAdviserUser: boolean, featureFlags: Record<string, boolean>) => {
    const role: MobileRole = isAdviserUser ? 'adviser' : 'standard'
    const bottomKeys = new Set(BOTTOM_NAV_KEYS[role])

    return APP_MOBILE_NAV_ITEMS.filter(
        (item) =>
            !bottomKeys.has(item.key) &&
            !EXCLUDED_MORE_KEYS.has(item.key) &&
            getFeatureFlaggedVisibleItems(item.featureFlag, featureFlags),
    )
}

export const isMoreRoute = (pathname: string, isAdviserUser: boolean, featureFlags: Record<string, boolean>) => {
    if (pathname === APP_ROUTES.MORE) return true

    return getMobileMoreNavItems(isAdviserUser, featureFlags).some((item) => item.isActive(pathname))
}

export const getMobileBottomNavItems = (
    isAdviserUser: boolean,
    featureFlags: Record<string, boolean>,
): MobileBottomNavItem[] => {
    const role: MobileRole = isAdviserUser ? 'adviser' : 'standard'
    const bottomKeys = new Set(BOTTOM_NAV_KEYS[role])

    const items = getVisibleItems(role)
        .filter((item) => bottomKeys.has(item.key))
        .map((item) => ({
            key: item.key,
            to: item.to,
            label: item.label,
            icon: item.icon,
            isActive: item.isActive,
        }))

    items.push({
        key: 'more',
        to: APP_ROUTES.MORE,
        label: NAVIGATION_COPY.mobileBottomNav.more,
        icon: Ellipsis,
        isActive: (pathname) => isMoreRoute(pathname, isAdviserUser, featureFlags),
    })

    return items
}
